import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Flex,
  Heading,
  Text,
  useBreakpointValue,
  useDisclosure,
  useToast
} from "@chakra-ui/react";
import { t } from "i18next";
import * as React from "react";
import { Fragment, useState } from "react";

import {
  directEmissionsKeys,
  useAddDirectEmissionsItem,
  useDefaultOrganizationQuestionnaireConfig,
  useDeleteDirectEmissionsItem,
  useFinalizeQuestionnaire,
  useGetDirectEmissionsItems,
  useUpdateDirectEmissionsItem
} from "../../../../hooks/query";
import { useQueryClient } from "react-query";
import { showErrorToast, showSuccessToast } from "../../../../utils";
import { ConfirmationDialog } from "../../../../components/ConfirmationDialog";
import { useDispatch } from "react-redux";
import { hideAppSpinner, showAppSpinner } from "../../../../context/Store/slices/appSpinnerSlice";
import { DimensionItemModal, DimensionsGrid } from "../DimensionsGrid";
import { DeleteDialog } from "../../../DataEntryPlanning/components/DeleteDialog";
import { mapEmissionsFormDataToApiData } from "../../helpers";

export const directEmissionsConsumptionTypes = [
  "stationaryConsumptionTypes",
  "mobileConsumptionTypes",
  "refrigerantConsumptionTypes"
];

function DirectEmissions({ organizationStructure, defaultValues, handleTabChange, hasNextScope, isPreview }) {
  const { id: questionnaireId, dimensionItems } = defaultValues;

  const queryClient = useQueryClient();
  const toast = useToast();
  const dispatch = useDispatch();

  const { isOpen: isConfirmDialogOpen, onOpen: onConfirmDialogOpen, onClose: onConfirmDialogClose } = useDisclosure();
  const {
    isOpen: isDimensionItemModalOpen,
    onOpen: onDimensionItemModalOpen,
    onClose: onDimensionItemModalClose
  } = useDisclosure();
  const {
    isOpen: isDeleteItemDialogOpen,
    onOpen: onDeleteItemDialogOpen,
    onClose: onDeleteItemDialogClose
  } = useDisclosure();

  const buttonsWidth = useBreakpointValue({
    base: "24",
    md: "32"
  });

  const STATIONARY_SECTION = "stationary";
  const MOBILE_SECTION = "mobile";
  const REFRIGERANT_SECTION = "refrigerant";

  const [selectedItem, setSelectedItem] = useState(null);
  const [dimensionItemModalData, setDimensionItemModalData] = useState(null);
  const [sectionsSize, setSectionsSize] = useState({});

  function changeTab() {
    handleTabChange(prev => prev + 1);
  }

  const { mutate: finalize } = useFinalizeQuestionnaire({
    ...useDefaultOrganizationQuestionnaireConfig(),
    onError: err => {
      showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message);
    }
  });

  const { mutate: addItem } = useAddDirectEmissionsItem({
    onSuccess: () => {
      handleDimensionItemModalClose();
      showSuccessToast(toast, t("organizationQuestionnaire.page.added"));
      queryClient.invalidateQueries(directEmissionsKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => {
      dispatch(hideAppSpinner());
    }
  });

  const { mutate: updateItem } = useUpdateDirectEmissionsItem({
    onSuccess: () => {
      handleDimensionItemModalClose();
      showSuccessToast(toast, t("organizationQuestionnaire.page.updated"));
      queryClient.invalidateQueries(directEmissionsKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => {
      dispatch(hideAppSpinner());
    }
  });

  const { mutate: deleteItem } = useDeleteDirectEmissionsItem({
    onSuccess: () => {
      handleDeleteItemDialogClose();
      showSuccessToast(toast, t("organizationQuestionnaire.page.deleted"));
      queryClient.invalidateQueries(directEmissionsKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => {
      dispatch(hideAppSpinner());
    }
  });

  const handleFinalizeConfirmation = () => {
    finalize(questionnaireId);
    onConfirmDialogClose();
  };

  const handleDeleteItemClick = (type, item) => {
    onDeleteItemDialogOpen();
    setSelectedItem({ id: item.id, type: type });
  };

  const deleteRecord = () => {
    dispatch(showAppSpinner());
    deleteItem({ questionnaireId, recordId: selectedItem.id, type: selectedItem.type });
  };

  const handleDeleteItemDialogClose = () => {
    setSelectedItem(null);
    onDeleteItemDialogClose();
  };

  const handleEditItemClick = (type, item) => {
    setDimensionItemModalData({
      organizationStructure,
      dimensionItems,
      scope: "one",
      fieldArrayName: type,
      consumptionTypesAttr: directEmissionsConsumptionTypes.find(el => el.includes(type)),
      editData: item
    });
    onDimensionItemModalOpen();
  };

  const handleAddItemClick = (type, sectionType) => {
    setDimensionItemModalData({
      organizationStructure,
      dimensionItems,
      scope: "one",
      fieldArrayName: sectionType,
      consumptionTypesAttr: type
    });
    onDimensionItemModalOpen();
  };

  const handleDimensionItemModalSubmit = (data) => {
    const apiData = mapEmissionsFormDataToApiData(data[dimensionItemModalData.fieldArrayName]);
    dispatch(showAppSpinner());
    if (dimensionItemModalData.editData) {
      updateItem({
        questionnaireId,
        recordId: apiData[0].id,
        data: apiData[0],
        type: dimensionItemModalData.fieldArrayName
      });
    } else {
      addItem({
        questionnaireId,
        data: apiData,
        type: dimensionItemModalData.fieldArrayName
      });
    }
  };

  const handleDimensionItemModalClose = () => {
    setDimensionItemModalData(null);
    onDimensionItemModalClose();
  };

  const handleSectionSizeChange = (section, size) => {
    setSectionsSize({
      ...sectionsSize,
      [section]: size
    });
  };

  const checkForNoDataAvailable = () => {
    if (organizationStructure) {
      const applicableTypes = directEmissionsConsumptionTypes.filter(type =>
        organizationStructure[type]?.length > 0);
      return isPreview && sectionsSize && Object.keys(sectionsSize).length === applicableTypes.length &&
        typeof Object.keys(sectionsSize).find(key => sectionsSize[key] > 0) === "undefined";
    }
    return false;
  };

  return (
    <Fragment>
      {checkForNoDataAvailable() ?
        <Text fontSize="lg" fontWeight="bold">
          {t("noDataAvailable")}
        </Text> :
        <Box marginBlockStart="10" overflow="auto">
          {organizationStructure && (organizationStructure.stationaryConsumptionTypes?.length > 0 ||
              organizationStructure.mobileConsumptionTypes?.length > 0 ||
              organizationStructure.refrigerantConsumptionTypes.length > 0) &&
            <Accordion defaultIndex={[0]} allowMultiple marginBottom="10">
              {directEmissionsConsumptionTypes.map(type => {
                if (organizationStructure[type]?.length > 0) {
                  if (isPreview && sectionsSize && Object.keys(sectionsSize).length > 0 && sectionsSize[type] === 0) {
                    return null;
                  }
                  const sectionTitleKey = type.split("ConsumptionTypes")[0];
                  let sectionType = "";
                  switch (sectionTitleKey) {
                    case "stationary":
                      sectionType = STATIONARY_SECTION;
                      break;
                    case "mobile":
                      sectionType = MOBILE_SECTION;
                      break;
                    case "refrigerant":
                      sectionType = REFRIGERANT_SECTION;
                      break;
                    default:
                      sectionType = "";
                  }

                  return <AccordionItem
                    key={`accordion-item-${type}`}
                    sx={{
                      ".chakra-collapse": {
                        padding: "20px 0"
                      }
                    }}
                  >
                    {
                      ({ isExpanded }) => {
                        return <>
                          <h2>
                            <AccordionButton>
                              <Box flex="1" textAlign="left">
                                <Heading color="esg_gray.200" size="xs">
                                  {t(`organizationQuestionnaire.scope1.inputs.${sectionTitleKey}Title`)}
                                </Heading>
                              </Box>
                              <Text fontSize={"sm"} mr={2}>
                                {isExpanded ? t("buttons.collapse") : t("buttons.expand")}
                              </Text>
                              <AccordionIcon />
                            </AccordionButton>
                          </h2>
                          <AccordionPanel overflow="auto">
                            {questionnaireId && organizationStructure && <DimensionsGrid
                              questionnaireId={questionnaireId}
                              scope="one"
                              organizationStructure={organizationStructure}
                              sectionType={sectionType}
                              consumptionTypesAttr={type}
                              getItemsFunction={useGetDirectEmissionsItems}
                              handleAddItem={() => handleAddItemClick(type, sectionType)}
                              handleEditItem={(dataItem) => handleEditItemClick(sectionType, dataItem)}
                              handleDeleteItem={(dataItem) => handleDeleteItemClick(sectionType, dataItem)}
                              isPreview={isPreview}
                              onDataItemsChange={handleSectionSizeChange}
                            />}
                          </AccordionPanel>
                        </>;
                      }
                    }
                  </AccordionItem>;
                }
                return null;
              })}
            </Accordion>
          }
        </Box>
      }
      {!isPreview &&
        <Flex
          justifyContent="space-between"
          marginBlockStart="10"
          position="sticky"
          bottom="0"
          backgroundColor="#fff"
          borderColor="esg_gray.100"
          bordersyle="solid"
          borderTopWidth="1px"
          paddingBlock="4"
          zIndex="10"
        >
          <Box>
            {hasNextScope && (
              <Button
                colorScheme="white"
                color="esg_lightgreen"
                border={"1px solid"}
                borderColor={"esg_lightgreen"}
                type="submit"
                width={buttonsWidth}
                onClick={() => changeTab()}
              >
                {t("buttons.next")}
              </Button>
            )}
          </Box>
          <Button
            colorScheme="customScheme"
            width={buttonsWidth}
            onClick={() => {
              onConfirmDialogOpen();
            }}
          >
            {t("buttons.finalize")}
          </Button>
        </Flex>
      }
      <ConfirmationDialog
        isOpen={isConfirmDialogOpen}
        onClose={onConfirmDialogClose}
        onConfirm={handleFinalizeConfirmation}
        headerText={t("questionnaireFinalizationDialog.header")}
        confirmationText={t("questionnaireFinalizationDialog.question")}
      />
      <DeleteDialog
        isOpen={isDeleteItemDialogOpen}
        onClose={handleDeleteItemDialogClose}
        onDelete={deleteRecord}
        entityName={"Item"}
      />
      {
        isDimensionItemModalOpen && dimensionItemModalData !== null && <DimensionItemModal
          modalData={dimensionItemModalData}
          isOpen={isDimensionItemModalOpen}
          onClose={handleDimensionItemModalClose}
          onSubmit={handleDimensionItemModalSubmit}
          scope="one"
        />
      }
    </Fragment>
  );
}

export default DirectEmissions;
